import type { Refund } from "@tdelivr/api-client/generated/finance/refund.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { money } from "./money.ts";
import { absent, at, field, optional, type RecordDetail } from "./record-detail.ts";

/**
 * What the goodwill board does to the refunds FIN-04 returns, away from any rendering.
 *
 * **This reads the same operation the finance console's refunds board reads**, and that is the
 * point rather than duplication: `GET /v1/refunds` is one listing, and the two consoles ask
 * different questions of it. Finance asks "is this refund right"; support asks "what did we give
 * this customer, and is it waiting on somebody". So the state words, the ordering and the columns
 * differ, and the arithmetic — which is the contract's, not a reading — is deliberately the same.
 *
 * **The `Case` column has no source, and it is the middle of the design's three.** The design writes
 * `cs_4455 · fare dispute`, `cs_4468 · lost item` — the case a refund was raised from. Nothing on
 * FIN-04 or on any of the 142 contracts links a refund to a case: there is no `caseId`, no
 * `sourceReference`, no `raisedFromCase`. SUP-04's customer-360 read model holds case links and is
 * entirely `/internal/v1`, and its only listing operation is a projection write. The cell draws the
 * refund's own `reasonCode` and says what is missing, because a case identifier inferred here would
 * be a link an agent clicks. R-35.
 *
 * **The design's whole governing rule has no source either.** The page's description is "Over the
 * agent limit goes to a supervisor", its tile reads `Awaiting supervisor · 2 · over KES 2,000`, and
 * its closing note is "An agent cannot approve a refund they raised." FIN-04's status enum has no
 * approval state — `REQUESTED` moves to `PROCESSING` or `CANCELLED` and nothing asks anybody — and
 * nothing publishes who raised a refund. So no row is ever drawn as awaiting a supervisor, and the
 * design's note is kept word for word because it is the product's rule whether or not the service
 * enforces it. That is the gap on this screen most worth closing.
 *
 * **Credits are not refunds, and the design draws both.** Its rows include `cr_1120 · KES 200.00
 * credit · issued`. Nothing on the platform models a goodwill credit: PAY-02's wallet operations
 * are `/internal/v1` and a wallet top-up is not a credit against a case. Every row here is a
 * refund, and the board says so rather than labelling refunds as credits.
 */

/**
 * How many refunds to take in one request.
 *
 * 200 is FIN-04's own maximum. The cursor is safe to follow because ADR 0027 added a `requested_at`
 * column no transition rewrites, with an index matching `(requested_at DESC, refund_id DESC)` — so
 * a refund that succeeds mid-page cannot move ahead of the cursor and then appear on no page.
 */
export const PER_FETCH = 200;

/**
 * The state class and the word, from `status`, in this console's words rather than finance's.
 *
 * | status            | class     | word              | why this word here                            |
 * | ----------------- | --------- | ----------------- | --------------------------------------------- |
 * | `REQUESTED`       | `idle`    | `raised`          | the design's `Raised today · 17` tile         |
 * | `PROCESSING`      | `active`  | `processing`      | the design's `active · processing` row        |
 * | `SUCCEEDED`       | `done`    | `issued`          | the design's `done · issued` row              |
 * | `FAILED`          | `failed`  | `rejected`        | the design's `Rejected · 1` tile              |
 * | `CANCELLED`       | `closed`  | `cancelled`       | the contract's own word                       |
 * | `OUTCOME_UNKNOWN` | `unknown` | `outcome unknown` | the contract's own word                       |
 *
 * **Three of the six words differ from the finance console's for the same operation**, and each is
 * this page's own design copy: `raised` where finance says `requested`, `issued` where finance says
 * `completed`, `rejected` where finance says `failed`. CLAUDE.md section 1 says keep the copy word
 * for word, and these are two different approved pages describing the same rows to two different
 * readers. The classes are identical, so the two boards never disagree about what a state *means*.
 *
 * **`FAILED` is `rejected` and that word is a trap worth naming.** The design's tile is `Rejected`,
 * which to an agent reads as "a supervisor said no" — and FIN-04's `FAILED` means the *provider*
 * refused it, with no reason published. The badge's title says which, because an agent who tells a
 * customer their refund was rejected by the company when the rail timed out has made it worse.
 */
export function stateOf(refund: Refund): { classKey: StateClassKey; label: string } {
  switch (refund.status) {
    case "REQUESTED":
      return { classKey: "idle", label: "raised" };
    case "PROCESSING":
      return { classKey: "active", label: "processing" };
    case "SUCCEEDED":
      return { classKey: "done", label: "issued" };
    case "FAILED":
      return { classKey: "failed", label: "rejected" };
    case "CANCELLED":
      return { classKey: "closed", label: "cancelled" };
    case "OUTCOME_UNKNOWN":
      return { classKey: "unknown", label: "outcome unknown" };
  }
}

/**
 * The design's `Amount` cell: the currency code and the full minor units.
 *
 * CLAUDE.md section 2, which is not a style rule here: an agent reading `30.00` beside `2,480.00`
 * with no currency on either has no way to tell a thirty-shilling goodwill gesture from a thirty
 * dollar one, and this board's rows are what a customer is told.
 */
export function amountCell(refund: Refund): string {
  return money(refund.amountMinor, refund.currency);
}

/**
 * What this refund is against, in the cell the design gives to a case.
 *
 * `reasonCode` is the nearest published thing to the design's `fare dispute` and `lost item`: it is
 * the reason recorded on the refund itself. The case identifier beside it has no source, which the
 * row's title says. Never a composed `cs_` string.
 */
export function againstCell(refund: Refund): string {
  return refund.reasonCode;
}

/** The sort options the design's toolbar offers, in its order and its words. */
export type GoodwillOrder = "default" | "state" | "az" | "za";

export interface GoodwillQuery {
  readonly search: string;
  readonly order: GoodwillOrder;
  /** 1-based, as the pager shows it. */
  readonly page: number;
  readonly perPage: number;
}

export interface GoodwillView {
  readonly rows: readonly Refund[];
  /** Every row that matched, across all pages — what an export writes. */
  readonly all: readonly Refund[];
  readonly total: number;
  readonly pageCount: number;
}

/** Every word on a row a search should match: the published values and the words on screen. */
function searchableText(refund: Refund): string {
  return [
    refund.refundId,
    refund.paymentIntentId,
    refund.paymentTransactionId ?? "",
    refund.reasonCode,
    refund.currency,
    refund.status,
    stateOf(refund).label,
    amountCell(refund),
  ]
    .join(" ")
    .toLowerCase();
}

/**
 * Filter, order and page what the screen holds.
 *
 * `default` is the order FIN-04 answered in — most recently requested first — which is what a
 * board titled "Refunds and credits raised from cases" implies. Q-69 records the assumption.
 */
export function goodwillPage(
  rows: readonly Refund[],
  query: GoodwillQuery,
  state?: string,
): GoodwillView {
  const needle = query.search.trim().toLowerCase();
  const matched = rows.filter(
    (refund) =>
      (state === undefined || stateOf(refund).label === state) &&
      (needle.length === 0 || searchableText(refund).includes(needle)),
  );

  const ordered =
    query.order === "default"
      ? matched
      : [...matched].sort((left, right) => {
          if (query.order === "state") {
            return stateOf(left).label.localeCompare(stateOf(right).label);
          }
          // "Name" is the first column, as the design's A–Z is. Here that is the request.
          const compared = left.refundId.localeCompare(right.refundId);
          return query.order === "az" ? compared : -compared;
        });

  const total = ordered.length;
  const pageCount = Math.max(1, Math.ceil(total / query.perPage));
  const page = Math.min(Math.max(1, query.page), pageCount);
  const from = (page - 1) * query.perPage;

  return { rows: ordered.slice(from, from + query.perPage), all: ordered, total, pageCount };
}

/** The states present in what came back, in board order, as the design's own `gridUI` does. */
export function statesPresent(rows: readonly Refund[]): readonly string[] {
  return [...new Set(rows.map((refund) => stateOf(refund).label))];
}

/**
 * The sentence this board owes an agent about what it actually fetched.
 *
 * It names the tenant scoping as well as the window, because this listing is the one place the
 * support console reads a finance service: FIN-04 scopes to the token's tenant, so these are the
 * tenant's refunds and not this agent's own.
 */
export function fetchedNote(fetched: number | undefined, more: boolean): string | undefined {
  if (fetched === undefined) return undefined;
  const held =
    `The count above is the ${String(fetched)} refunds this board has fetched, not the tenant's ` +
    `total — FIN-04 publishes a cursor and no count. The search and the state filter cover what ` +
    `is fetched rather than asking the service.`;
  return more
    ? `${held} Next fetches older refunds from the last one shown; FIN-04 orders by a requested-at column that no transition rewrites, so no refund can cross the cursor between pages.`
    : `${held} There are no older refunds: the service answered no cursor, so this is all of them.`;
}

/**
 * The selected refund, for the panel beside the board.
 *
 * **The case is an absent field rather than a missing one.** `absent()` draws the design's `—` in
 * `--text3` with the reason on the title, which is the treatment this repository uses wherever a
 * designed field has no source — so the panel reads as complete-and-honest rather than as
 * truncated, and the absence is answerable by whoever picks it up.
 *
 * **The three amounts are drawn, as the finance console draws them.** A refund is only readable
 * against what was captured and what has already gone back: `KES 500.00` means nothing until you
 * know whether `KES 2,000.00` was captured and `KES 1,500.00` already refunded. On this console it
 * matters for a different reason — an agent about to tell a customer what they will get.
 */
export function goodwillDetail(refund: Refund): RecordDetail {
  const state = stateOf(refund);
  const remaining = refund.capturedAmountMinor - refund.previouslySucceededMinor;

  return {
    headline: money(refund.amountMinor, refund.currency),
    identifier: refund.refundId,
    source: "FIN-04",
    state: { ...state, code: "FIN-04", status: refund.status },
    fields: [
      absent(
        "Case",
        "Nothing on FIN-04, or on any contract, links a refund to the case it was raised from — no caseId, no sourceReference. SUP-04 holds case links and is entirely /internal/v1. See R-35.",
      ),
      field("Reason", refund.reasonCode, true),
      field("This refund", money(refund.amountMinor, refund.currency)),
      field("Captured on the payment", money(refund.capturedAmountMinor, refund.currency)),
      field("Already refunded", money(refund.previouslySucceededMinor, refund.currency)),
      field("Still refundable", money(remaining, refund.currency)),
      field("Payment intent", refund.paymentIntentId, true),
      ...optional("Payment transaction", refund.paymentTransactionId, true),
      absent(
        "Raised by",
        "FIN-04 publishes no actor on a refund, so the design's rule that an agent cannot approve one they raised cannot be checked here. See R-35.",
      ),
      field("Raised", at(refund.requestedAt) ?? refund.requestedAt),
      ...optional("Completed", at(refund.completedAt)),
      field("Last changed", at(refund.updatedAt) ?? refund.updatedAt),
      field("Version", `v${String(refund.version)}`),
    ],
    note:
      refund.status === "OUTCOME_UNKNOWN"
        ? "The provider call did not answer, so neither success nor failure is true yet. Do not tell the customer either one, and do not raise a second refund — FIN-04 resolves this on its own."
        : "Every row on this board is a refund. Nothing on the platform models a goodwill credit, which the design also draws.",
  };
}
